import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Wand2, ArrowRight, Loader2, RefreshCcw } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const GOALS = [
  { key: "energy", label: "Boost energy & focus" },
  { key: "immunity", label: "Strengthen immunity" },
  { key: "hangover", label: "Hangover recovery" },
  { key: "athletic", label: "Athletic recovery" },
  { key: "beauty", label: "Beauty & glow" },
  { key: "weight", label: "Weight loss support" },
  { key: "migraine", label: "Migraine relief" },
  { key: "anti-aging", label: "Anti-aging / longevity" },
  { key: "detox", label: "Detox & cleanse" },
  { key: "jet-lag", label: "Jet lag recovery" },
];

const SYMPTOMS = [
  "Fatigue", "Brain fog", "Dehydration", "Headache",
  "Muscle soreness", "Stress", "Poor sleep", "Recent travel",
  "Cold/flu symptoms", "Skin issues",
];

export default function TreatmentRecommender({ trigger }) {
  const [open, setOpen] = useState(false);
  const [goal, setGoal] = useState("");
  const [symptoms, setSymptoms] = useState([]);
  const [context, setContext] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const nav = useNavigate();

  const toggleSym = (s) => setSymptoms(symptoms.includes(s) ? symptoms.filter((x) => x !== s) : [...symptoms, s]);

  const run = async () => {
    if (!goal) return;
    setLoading(true);
    try {
      const { data } = await api.post("/ai/treatment-recommend", { goal, symptoms, context });
      setResult(data);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setResult(null); setGoal(""); setSymptoms([]); setContext(""); };

  const findProviders = () => {
    setOpen(false);
    nav(`/search?service=${encodeURIComponent(result.treatment)}`);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="bg-[#064E3B] hover:bg-[#053d2f] rounded-full" data-testid="recommender-trigger">
            <Wand2 className="w-4 h-4 mr-2" /> Find my treatment
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#10B981]" /> AI Treatment Recommender
          </DialogTitle>
          <DialogDescription>Tell us what brings you in today and we'll suggest the right IV therapy.</DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div>
              <div className="text-xs uppercase font-semibold tracking-wider text-stone-700 mb-2">What's your main goal?</div>
              <div className="grid grid-cols-2 gap-2">
                {GOALS.map((g) => (
                  <button
                    key={g.key}
                    onClick={() => setGoal(g.label)}
                    className={`px-3 py-2 text-sm rounded-lg border text-left transition ${goal === g.label ? "bg-[#064E3B] text-white border-[#064E3B]" : "bg-white border-stone-200 hover:border-[#064E3B]/40"}`}
                    data-testid={`recommender-goal-${g.key}`}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase font-semibold tracking-wider text-stone-700 mb-2">Any symptoms? (select all that apply)</div>
              <div className="flex flex-wrap gap-1.5">
                {SYMPTOMS.map((s) => (
                  <button
                    key={s}
                    onClick={() => toggleSym(s)}
                    className={`px-2.5 py-1 text-xs rounded-full border transition ${symptoms.includes(s) ? "bg-[#10B981] text-white border-[#10B981]" : "bg-white border-stone-200 hover:border-[#064E3B]/40"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase font-semibold tracking-wider text-stone-700 mb-2">Anything else? (optional)</div>
              <Textarea
                rows={2}
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="Big event tomorrow, just got back from a flight, etc."
              />
            </div>

            <Button onClick={run} disabled={!goal || loading} className="w-full bg-[#064E3B] hover:bg-[#053d2f]" data-testid="recommender-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Recommend a treatment <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
            <p className="text-[10px] text-stone-400 text-center">For informational purposes only. Not medical advice. Always consult a qualified provider.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-2xl bg-gradient-to-br from-[#064E3B] to-[#0a6b54] text-white p-5">
              <div className="text-xs uppercase tracking-wider text-white/80 mb-2">Recommended for you</div>
              <h3 className="font-display text-2xl font-semibold mb-2">{result.treatment}</h3>
              <p className="text-sm text-white/90 leading-relaxed mb-3">{result.reason}</p>
              {result.key_benefits?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {result.key_benefits.map((b) => (
                    <Badge key={b} className="bg-white/15 hover:bg-white/15 border-0 text-white text-xs font-normal">{b}</Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={reset} className="flex-1"><RefreshCcw className="w-4 h-4 mr-1.5" /> Try again</Button>
              <Button onClick={findProviders} className="flex-1 bg-[#064E3B] hover:bg-[#053d2f]" data-testid="recommender-find-providers">
                Find providers <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
